import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { ReactComponent as ToolTip } from "images/toolip.svg";

export default function ActionTooltip({
  label,
  children,
  placement = "top",
  offset = 10,
  className = "",
  zIndex = 99999,
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);

  const updateRect = () => {
    if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
  };

  useEffect(() => {
    if (!open) return;
    updateRect();
    const onScroll = () => updateRect();
    const onResize = () => updateRect();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const tooltipNode =
    label && open && rect
      ? createPortal(
          <div
            role="tooltip"
            className={`pointer-events-none fixed whitespace-nowrap px-[14px] py-[12px] rounded-[6px]   bg-white text-[12px] leading-none text-[#70707B] shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.10),0px_2px_4px_-2px_rgba(16,24,40,0.06)] ${className}`}
            style={{
              zIndex,
              left:
                placement === "left"
                  ? rect.left - offset
                  : placement === "right"
                  ? rect.right + offset
                  : rect.left + rect.width / 2,
              top:
                placement === "top"
                  ? rect.top - offset
                  : placement === "bottom"
                  ? rect.bottom + offset
                  : rect.top + rect.height / 2,
              transform:
                placement === "top"
                  ? "translate(-50%, -100%)"
                  : placement === "bottom"
                  ? "translate(-50%, 0)"
                  : placement === "left"
                  ? "translate(-100%, -50%)"
                  : "translate(0, -50%)",
            }}
          >
            {label}
            {placement === "top" && (
              <span
                className="absolute left-1/2 translate-x-[-50%]"
                style={{ top: "100%" }}
              >
                <ToolTip />
              </span>
            )}
            {placement === "bottom" && (
              <span
                className="absolute left-1/2 translate-x-[-50%]"
                style={{ bottom: "100%" }}
              >
                <ToolTip />
              </span>
            )}

            {placement === "right" && (
              <span
                className="absolute top-1/2 -translate-y-1/2"
                style={{ right: "calc(100% - 5px)" }}
              >
                <ToolTip className="rotate-90 block origin-center " />
              </span>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <span
      ref={anchorRef}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {tooltipNode}
    </span>
  );
}

ActionTooltip.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  offset: PropTypes.number,
  className: PropTypes.string,
  zIndex: PropTypes.number,
};
